import { useMemo } from "react";
import {
  Collaborator,
  EscalationReason,
  ExploreCustomField,
  ExploreEnrichment,
  ExploreInteractionRow,
  ExploreInteractionType,
  OriginAuthor,
  ResolutionStatus,
  ResolvedReason,
  Scorecard,
  SkipReason,
  Team,
} from "@/v2/entities/explore/Explore.model";
import { Flex, DataList, ScrollArea, Spinner } from "@radix-ui/themes";
import { DateFormatter } from "@/v2/components/formatters/DateFormatter";
import { NumberFormatter } from "@/v2/components/formatters/NumberFormatter";
import { StatusFormatter } from "@/v2/components/formatters/StatusFormatter";
import { ChannelFormatter } from "@/v2/components/formatters/ChannelFormatter";
import { TopicFormatter } from "@/v2/components/formatters/TopicFormatter";
import { CategoryFormatter } from "@/v2/components/formatters/CategoryFormatter";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import { CollaboratorsFormatter } from "@/v2/entities/explore/formatters/CollaboratorsFormatter";
import { TeamFormatter } from "@/v2/entities/explore/formatters/TeamFormatter";
import styles from "./Overview.module.scss";
import { TldrFormatter } from "@/v2/components/formatters/TldrFormatter";
import { DurationFormatter } from "@/v2/components/formatters/DurationFromatter";
import {
  ExploreColumnType,
  exploreColumnTypeToLabel,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import {
  visibleConversationColumns,
  visibleTicketColumns,
} from "@/v2/entities/explore/columns/useExploreColumns";
import cx from "classnames";
import { EscalationReasonFormatter } from "@/v2/entities/explore/formatters/EscalationReasonFormatter";
import { TextWithTooltip } from "@/components/shared/tooltip";
import { TimeToFirstResponseFormatter } from "@/v2/entities/explore/formatters/TimeToFirstResponseFormatter";
import { TimeToResolutionFormatter } from "@/v2/entities/explore/formatters/TimeToResolutionFormatter";
import { ResolutionStatusFormatter } from "@/v2/entities/explore/formatters/ResolutionStatusFormatter";
import { SkipReasonFormatter } from "@/v2/entities/explore/formatters/SkipReasonFormatter";
import { ResolvedReasonFormatter } from "@/v2/entities/explore/formatters/ResolvedReasonFormatter";
import { OriginAuthorFormatter } from "@/v2/entities/explore/formatters/OriginAuthorFormatter";
import { TicketIdFormatter } from "@/v2/entities/explore/formatters/TicketIdFormatter";

const dataListValueTypes = [
  "string",
  "number",
  "date",
  "boolean",
  "duration",
  "status",
  "channel",
  "topic",
  "category",
  "avgQaScore",
  "resolutionStatus",
  "escalationReason",
  "skipReason",
  "resolvedReason",
  "collaborators",
  "team",
  "tldr",
  "timeToResolution",
  "timeToFirstResponse",
  "originAuthor",
  "ticketId",
] as const;
type DataListValueType = (typeof dataListValueTypes)[number];

interface DataListValueBase {
  label: string;
  type: DataListValueType;
  loading?: boolean;
  tooltip?: boolean;
}

interface StringDataListValue extends DataListValueBase {
  type: "string";
  value?: string | null;
}

interface NumberDataListValue extends DataListValueBase {
  type: "number";
  value?: number | null;
}

interface DateDataListValue extends DataListValueBase {
  type: "date";
  value?: string | null;
}

interface BooleanDataListValue extends DataListValueBase {
  type: "boolean";
  value?: boolean | null;
}

interface DurationDataListValue extends DataListValueBase {
  type: "duration";
  value?: number | null;
}

interface StatusDataListValue extends DataListValueBase {
  type: "status";
  value?: string | null;
}

interface ChannelDataListValue extends DataListValueBase {
  type: "channel";
  value?: string | null;
}

interface TopicDataListValue extends DataListValueBase {
  type: "topic";
  value?: string | null;
}

interface CategoryDataListValue extends DataListValueBase {
  type: "category";
  value?: string | null;
}

interface AvgQaScoreDataListValue extends DataListValueBase {
  type: "avgQaScore";
  value?: number | null;
  scorecards?: Scorecard[];
}

interface ResolutionStatusDataListValue extends DataListValueBase {
  type: "resolutionStatus";
  value?: ResolutionStatus | null;
}

interface EscalationReasonDataListValue extends DataListValueBase {
  type: "escalationReason";
  value?: EscalationReason | null;
}

interface ResolvedReasonDataListValue extends DataListValueBase {
  type: "resolvedReason";
  value?: ResolvedReason | null;
}

interface SkipReasonDataListValue extends DataListValueBase {
  type: "skipReason";
  value?: SkipReason | null;
}

interface CollaboratorsDataListValue extends DataListValueBase {
  type: "collaborators";
  value?: Collaborator[] | null;
}

interface TeamDataListValue extends DataListValueBase {
  type: "team";
  value?: Team[] | null;
}

interface TldrDataListValue extends DataListValueBase {
  type: "tldr";
  value?: string | null;
}

interface TimeToResolutionDataListValue extends DataListValueBase {
  type: "timeToResolution";
  value?: number | null;
}

interface TimeToFirstResponseDataListValue extends DataListValueBase {
  type: "timeToFirstResponse";
  value?: number | null;
}

interface originAuthorDataListValue extends DataListValueBase {
  type: "originAuthor";
  value?: OriginAuthor | null;
}

interface TicketIdDataListValue extends DataListValueBase {
  type: "ticketId";
  value?: string | null;
}

type DataListValue =
  | StringDataListValue
  | NumberDataListValue
  | DateDataListValue
  | BooleanDataListValue
  | DurationDataListValue
  | StatusDataListValue
  | ChannelDataListValue
  | TopicDataListValue
  | CategoryDataListValue
  | AvgQaScoreDataListValue
  | ResolutionStatusDataListValue
  | EscalationReasonDataListValue
  | SkipReasonDataListValue
  | ResolvedReasonDataListValue
  | CollaboratorsDataListValue
  | TeamDataListValue
  | TldrDataListValue
  | TimeToResolutionDataListValue
  | TimeToFirstResponseDataListValue
  | originAuthorDataListValue
  | TicketIdDataListValue;

const formatterFactory = (item: DataListValue) => {
  if (item.loading) {
    return <Spinner />;
  }

  if (item.value == null) {
    return "-";
  }

  switch (item.type) {
    case "string":
      return <>{item.value}</>;
    case "number":
      return <NumberFormatter value={item.value} />;
    case "date":
      return <DateFormatter value={item.value} />;
    case "boolean":
      return item.value ? "Yes" : "No";
    case "duration":
      return <DurationFormatter value={item.value} />;
    case "status":
      return <StatusFormatter value={item.value} size="2" />;
    case "channel":
      return <ChannelFormatter value={item.value} />;
    case "topic":
      return <TopicFormatter value={item.value} />;
    case "category":
      return <CategoryFormatter value={item.value} />;
    case "avgQaScore":
      return (
        <AvgQaScoreFormatter value={item.value} scorecards={item.scorecards} />
      );
    case "resolutionStatus":
      return <ResolutionStatusFormatter value={item.value} />;
    case "escalationReason":
      return <EscalationReasonFormatter value={item.value} />;
    case "skipReason":
      return <SkipReasonFormatter value={item.value} />;
    case "resolvedReason":
      return <ResolvedReasonFormatter value={item.value} />;
    case "collaborators":
      return <CollaboratorsFormatter value={item.value} />;
    case "team":
      return <TeamFormatter value={item.value} />;
    case "tldr":
      return (
        <Flex py="2">
          <TldrFormatter value={item.value} />
        </Flex>
      );

    case "timeToResolution":
      return <TimeToResolutionFormatter value={item.value} />;
    case "timeToFirstResponse":
      return <TimeToFirstResponseFormatter value={item.value} />;
    case "originAuthor":
      return <OriginAuthorFormatter value={item.value} />;
    case "ticketId":
      return <TicketIdFormatter value={item.value} />;
    default:
      return "";
  }
};

interface OverviewProps {
  source: ExploreInteractionType;
  interaction: ExploreInteractionRow;
  enrichments?: ExploreEnrichment[];
  customFields?: ExploreCustomField[];
  summary?: string;
  loadingSummary?: boolean;
  visibleColumns?: ExploreColumnType[];
  lite?: boolean;
  onCreateEnrichment?: (data: {
    task: string;
    name: string;
    apiResponse: any;
  }) => Promise<void>;
  isCreatingEnrichment?: boolean;
}

export function Overview({
  source,
  interaction,
  enrichments = [],
  customFields = [],
  summary,
  loadingSummary,
  lite = false,
  onCreateEnrichment,
  isCreatingEnrichment,
  visibleColumns = source === "tickets"
    ? visibleTicketColumns
    : visibleConversationColumns,
}: OverviewProps) {
  const visibleColumnsWithEnrichmentsAndCustomFields = useMemo(() => {
    return [
      ...visibleColumns,
      ...enrichments.map((enrichment) => enrichment.id as ExploreColumnType),
      ...customFields.map((customField) => customField.id as ExploreColumnType),
    ];
  }, [visibleColumns, enrichments, customFields]);

  const dataListItems = useMemo<DataListValue[]>(() => {
    const items: (DataListValue & { key: string })[] = [
      {
        key: "createdAt",
        type: "date",
        label: exploreColumnTypeToLabel.createdAt,
        value: interaction.createdAt,
      },
      {
        key: "updatedAt",
        type: "date",
        label: exploreColumnTypeToLabel.updatedAt,
        value: interaction.updatedAt,
      },
      {
        key: "status",
        type: "status",
        label: exploreColumnTypeToLabel.status,
        value: interaction.status,
      },
      {
        key: "channel",
        type: "channel",
        label: exploreColumnTypeToLabel.channel,
        value: interaction.channel,
      },
      {
        key: "topic",
        type: "topic",
        label: exploreColumnTypeToLabel.topic,
        value: interaction.topic,
      },
      {
        key: "subTopic",
        type: "string",
        label: exploreColumnTypeToLabel.subTopic,
        value: interaction.subTopic,
      },
      {
        key: "category",
        type: "category",
        label: exploreColumnTypeToLabel.category,
        value: interaction.category,
      },
      {
        key: "agentAssigned",
        type: "string",
        label: exploreColumnTypeToLabel.agentAssigned,
        value: interaction.agentAssigned,
      },
      {
        key: "team",
        type: "team",
        label: exploreColumnTypeToLabel.team,
        value: interaction.team,
        tooltip: false,
      },
      {
        key: "tenantUser",
        type: "string",
        label: exploreColumnTypeToLabel.tenantUser,
        value: interaction.tenantUser,
      },
      {
        key: "collaborators",
        type: "collaborators",
        label: exploreColumnTypeToLabel.collaborators,
        value: interaction.collaborators,
        tooltip: false,
      },
      {
        key: "avgQaScore",
        type: "avgQaScore",
        label: exploreColumnTypeToLabel.avgQaScore,
        value: interaction.avgQaScore,
        scorecards: interaction.scorecards,
        tooltip: false,
      },
      {
        key: "avgAutoQaScore",
        type: "avgQaScore",
        label: exploreColumnTypeToLabel.avgAutoQaScore,
        value: interaction.avgAutoQaScore,
        scorecards: interaction.scorecards,
        tooltip: false,
      },
      {
        key: "avgSentiment",
        type: "number",
        label: exploreColumnTypeToLabel.avgSentiment,
        value: interaction.avgSentiment,
      },
      {
        key: "avgQuackMessageScore",
        type: "avgQaScore",
        label: exploreColumnTypeToLabel.avgQuackMessageScore,
        value: interaction.avgQuackMessageScore,
      },
      {
        key: "csat",
        type: "number",
        label: exploreColumnTypeToLabel.csat,
        value: interaction.csat,
      },
      {
        key: "csatComment",
        type: "string",
        label: exploreColumnTypeToLabel.csatComment,
        value: interaction.csatComment,
      },
      {
        key: "ticketId",
        type: "ticketId",
        label: exploreColumnTypeToLabel.ticketId,
        value: interaction.ticketId,
      },
      {
        key: "countPublicMessages",
        type: "number",
        label: exploreColumnTypeToLabel.countPublicMessages,
        value: interaction.countPublicMessages,
      },
      {
        key: "countInternalMessages",
        type: "number",
        label: exploreColumnTypeToLabel.countInternalMessages,
        value: interaction.countInternalMessages,
      },
      {
        key: "timeToResolution",
        type: "timeToResolution",
        label: exploreColumnTypeToLabel.timeToResolution,
        value: interaction.timeToResolution,
      },
      {
        key: "timeToFirstResponse",
        type: "timeToFirstResponse",
        label: exploreColumnTypeToLabel.timeToFirstResponse,
        value: interaction.timeToFirstResponse,
      },
      {
        key: "resolutionStatus",
        type: "resolutionStatus",
        label: exploreColumnTypeToLabel.resolutionStatus,
        value: interaction.resolutionStatus,
      },

      {
        key: "escalationReason",
        type: "escalationReason",
        label: exploreColumnTypeToLabel.escalationReason,
        value: interaction.escalationReason,
      },
      {
        key: "skipReason",
        type: "skipReason",
        label: exploreColumnTypeToLabel.skipReason,
        value: interaction.skipReason,
      },
      {
        key: "resolvedReason",
        type: "resolvedReason",
        label: exploreColumnTypeToLabel.resolvedReason,
        value: interaction.resolvedReason,
      },
      {
        key: "countQuackMessages",
        type: "number",
        label: exploreColumnTypeToLabel.countQuackMessages,
        value: interaction.countQuackMessages,
      },
      {
        key: "isQuackEscalated",
        type: "boolean",
        label: exploreColumnTypeToLabel.isQuackEscalated,
        value: interaction.isQuackEscalated,
      },
      {
        key: "isQuackAnswered",
        type: "boolean",
        label: exploreColumnTypeToLabel.isQuackAnswered,
        value: interaction.isQuackAnswered,
      },
      {
        key: "originAuthor",
        type: "originAuthor",
        label: exploreColumnTypeToLabel.originAuthor,
        value: interaction.originAuthor,
      },
      {
        key: "originUrl",
        type: "string",
        label: exploreColumnTypeToLabel.originUrl,
        value: interaction.originUrl,
      },
      {
        key: "isUserAuthenticated",
        type: "boolean",
        label: exploreColumnTypeToLabel.isUserAuthenticated,
        value: interaction.isUserAuthenticated,
      },
      {
        key: "isAnsweredByAui",
        type: "boolean",
        label: exploreColumnTypeToLabel.isAnsweredByAui,
        value: interaction.isAnsweredByAui,
      },
    ];

    const addedDynamicKeys = new Set<string>();

    enrichments.forEach((enrichment) => {
      const value =
        interaction[enrichment.value as keyof ExploreInteractionRow];

      if (typeof value === "string") {
        items.push({
          key: enrichment.id,
          type: "string",
          label: `✨ ${enrichment.label}`,
          value: enrichment.options.find((option) => option.value === value)
            ?.name,
        });
        addedDynamicKeys.add(enrichment.id);
      }
    });

    customFields.forEach((customField: ExploreCustomField) => {
      if (addedDynamicKeys.has(customField.id)) {
        return;
      }
      // use custom field label
      const value =
        interaction[customField.label as keyof ExploreInteractionRow];

      if (typeof value === "string") {
        items.push({
          key: customField.id,
          type: "string",
          label: `✨ ${customField.label}`,
          value: customField.options.find((option) => option.value === value)
            ?.name,
        });
        addedDynamicKeys.add(customField.id);
      }
    });

    const visibleColumnsMap = new Map<string, number>(
      visibleColumnsWithEnrichmentsAndCustomFields.map((item, index) => [
        item,
        index,
      ]),
    );

    const visibleItems: DataListValue[] = items
      .filter(({ key }) => visibleColumnsMap.has(key))
      .sort(
        (a, b) => visibleColumnsMap.get(a.key)! - visibleColumnsMap.get(b.key)!,
      )
      .map(({ key, ...item }) => {
        return item;
      });

    visibleItems.unshift({
      type: "tldr",
      label: "Summary",
      value: summary,
      loading: loadingSummary,
      tooltip: false,
    });

    return visibleItems;
  }, [
    interaction,
    summary,
    loadingSummary,
    enrichments,
    customFields,
    visibleColumnsWithEnrichmentsAndCustomFields,
    onCreateEnrichment,
    isCreatingEnrichment,
    lite,
  ]);

  return (
    <Flex
      width={"100%"}
      height={"100%"}
      px={lite ? undefined : "6"}
      direction={"column"}
      overflow={"hidden"}
    >
      <ScrollArea scrollbars="vertical">
        <DataList.Root
          className={cx(styles.OverviewDataList, {
            [styles.lite]: lite,
          })}
        >
          {dataListItems.map((item, index) => (
            <DataList.Item key={index} align="center" className={styles.Row}>
              <DataList.Label minWidth="88px">{item.label}</DataList.Label>
              <DataList.Value className={styles.Value}>
                <TextWithTooltip
                  size="2"
                  color="gray"
                  highContrast
                  truncate
                  showTooltip={item.tooltip}
                >
                  {formatterFactory(item)}
                </TextWithTooltip>
              </DataList.Value>
            </DataList.Item>
          ))}
          {/* <DataList.Item align="center" className={styles.Row}>
            <AddEnrichmentField
              onSubmit={onCreateEnrichment!}
              loading={isCreatingEnrichment}
            />
          </DataList.Item> */}
        </DataList.Root>
      </ScrollArea>
    </Flex>
  );
}
